import { useState, useMemo } from "react";
import CalculatorShell from "@/components/CalculatorShell";
import Field from "@/components/Field";
import ResultStat from "@/components/ResultStat";
import { toNum, savingsMonthlyRequired, futureValueCompound } from "@/lib/finance";
import { fmtUSD } from "@/lib/format";

export default function SavingsGoalPage() {
    const [target, setTarget] = useState(50000);
    const [current, setCurrent] = useState(5000);
    const [years, setYears] = useState(5);
    const [rate, setRate] = useState(4.5);

    const { required, projected } = useMemo(() => {
        const required = savingsMonthlyRequired(toNum(target), toNum(current), toNum(rate), toNum(years));
        const projected = futureValueCompound(toNum(current), required, toNum(rate), toNum(years));
        return { required, projected };
    }, [target, current, years, rate]);

    return (
        <CalculatorShell
            slug="savings-goal"
            explainer={
                                <>
                    <h2>How this savings goal calculator works</h2>
                    <p>
                        A savings goal calculator tells you exactly how much to set aside each month to reach
                        a target amount by a specific date, accounting for interest earned along the way. Use
                        it for any goal — down payment, vacation, emergency fund, or major purchase.
                    </p>
                    <h3>How it works</h3>
                    <ul>
                        <li><strong>Target amount:</strong> the end goal in today's dollars — what you need to have saved by the deadline.</li>
                        <li><strong>Current savings:</strong> reduces the total contributions required because your existing balance compounds forward.</li>
                        <li><strong>Required monthly contribution:</strong> derived from a formula that accounts for compounding on both existing savings and new deposits.</li>
                        <li><strong>Time horizon:</strong> the single biggest driver of required monthly savings — the earlier you start, the less you need to put in each month.</li>
                        <li><strong>Interest rate:</strong> reduces the total you need to contribute; even a 4–5% high-yield savings rate makes a meaningful difference.</li>
                    </ul>
                    <h3>Tips</h3>
                    <ul>
                        <li>Set up an automatic transfer on payday so saving happens before you have a chance to spend it.</li>
                        <li>Use a high-yield savings account for goals under two years — low risk, meaningful interest.</li>
                        <li>Break large goals into milestones and celebrate each one to maintain motivation over a long timeline.</li>
                    </ul>
                </>
            }
        >
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
                <div className="lg:col-span-3 bg-card rounded-xl border border-border p-6 sm:p-8 space-y-5">
                    <Field label="Savings goal" prefix="$" value={target} onChange={setTarget} dataTestId="calc-input-target" />
                    <Field label="Currently saved" prefix="$" value={current} onChange={setCurrent} dataTestId="calc-input-current" />
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Time horizon (years)" value={years} onChange={setYears} step={0.5} dataTestId="calc-input-years" />
                        <Field label="Expected APY" suffix="%" value={rate} onChange={setRate} step={0.25} dataTestId="calc-input-rate" />
                    </div>
                </div>
                <div className="lg:col-span-2">
                    <ResultStat
                        label="Save each month"
                        value={required}
                        format={(n) => fmtUSD(n)}
                        secondary={[
                            { label: "Projected balance", value: projected, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-projected" },
                            { label: "You'll contribute", value: required * toNum(years) * 12, format: (n) => fmtUSD(n, { decimals: 0 }), testId: "calc-result-contrib" },
                        ]}
                    />
                </div>
            </div>
        </CalculatorShell>
    );
}
